import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Map, Marker, Source, Layer, Popup } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  Tooltip,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PropTypes from 'prop-types';

import { LocationsTabs } from './LocationsTabs';
import axiosInstance from 'utils/axiosUtil';

const mapboxToken =
  'pk.eyJ1IjoiZG9jdGl2aXR5IiwiYSI6ImNsMmo2OWx6bTBtdmUza3J6eDQ2Z3pvdG0ifQ.o9j6xJxOa2WKQLYdqQaJRQ';

// Create a circle GeoJSON for the search radius
const createCircle = (center, radiusMiles) => {
  const points = 64;
  const km = radiusMiles * 1.60934;
  const coords = [];
  
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = km * Math.cos(angle);
    const dy = km * Math.sin(angle);
    const lat = center.lat + (dy / 111.32);
    const lng = center.lng + (dx / (111.32 * Math.cos(center.lat * Math.PI / 180)));
    coords.push([lng, lat]);
  }
  coords.push(coords[0]); // Close the circle
  
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords]
    }
  };
};

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
    height: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    overflow: 'visible',
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 300,
  },
  searchField: {
    flex: 1,
    maxWidth: 500,
  },
  rightControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(3),
    fontSize: 14,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
  },
  limitInput: {
    width: 120,
  },
  panHint: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontStyle: 'italic',
  },
  mapContainer: {
    flex: 1,
    minHeight: 400,
    borderRadius: 4,
    overflow: 'visible',
    '& .mapboxgl-popup': {
      zIndex: '1000 !important',
    },
    '& .mapboxgl-popup-content': {
      maxHeight: '500px !important',
      overflowY: 'auto !important',
    },
  },
  centerMarker: {
    color: '#00A887',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
  },
  locationMarker: {
    color: '#1976d2',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
    cursor: 'pointer',
  },
  tooltipContent: {
    padding: theme.spacing(1),
  },
  tooltipName: {
    fontWeight: 500,
    marginBottom: 4,
  },
  tooltipAddress: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  tooltipStats: {
    fontSize: 12,
    marginTop: 4,
  },
  clusterItem: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: 4,
    marginTop: 4,
  },
  clusterItemName: {
    fontWeight: 500,
    fontSize: 12,
  },
  clusterItemCounts: {
    display: 'flex',
    gap: 4,
    marginTop: 2,
  },
  providerBadge: {
    backgroundColor: '#1976d2',
    color: '#fff',
    padding: '1px 4px',
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 500,
  },
  contactBadge: {
    backgroundColor: '#9c27b0',
    color: '#fff',
    padding: '1px 4px',
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 500,
  },
  orgBadge: {
    backgroundColor: '#00A887',
    color: '#fff',
    padding: '1px 4px',
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 500,
  },
  moreText: {
    fontSize: 11,
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    marginTop: 6,
  },
  popupContent: {
    maxHeight: 200,
    overflowY: 'auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
});

function LocationsMapPage({ classes }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.user);
  
  const mapRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [limit, setLimit] = useState(100);
  const [radius, setRadius] = useState(50);
  const [mapCenter, setMapCenter] = useState(null);
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [centerLocation, setCenterLocation] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);

  // Check admin access
  if (!user?.is_admin) {
    return (
      <>
        <LocationsTabs tab="locations_map" />
        <div className={classes.container}>
          <Card>
            <CardContent>
              <Typography>Access denied. Admin privileges required.</Typography>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Get initial address from URL params
  useEffect(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const address = searchParams.get('address');
    const locationId = searchParams.get('locationId');

    if (lat && lng) {
      const latitude = parseFloat(lat);
      const longitude = parseFloat(lng);
      if (!isNaN(latitude) && !isNaN(longitude)) {
        setMapCenter({ lat: latitude, lng: longitude });
        if (address) {
          setSearchAddress(address);
        }
        if (locationId) {
          setCenterLocation({ id: parseInt(locationId, 10) });
        }
        fetchNearbyLocations(latitude, longitude, limit, locationId ? parseInt(locationId, 10) : null);
        setInitialLoad(false);
        return;
      }
    }

    // Default to user's location if no params
    if (navigator.geolocation && initialLoad) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter({ lat: latitude, lng: longitude });
          fetchNearbyLocations(latitude, longitude, limit);
          setInitialLoad(false);
        },
        () => {
          // Default to US center if geolocation fails
          setMapCenter({ lat: 39.8283, lng: -98.5795 });
          setInitialLoad(false);
        }
      );
    }
  }, [searchParams]);

  const fetchNearbyLocations = async (lat, lng, maxResults, excludeId = null, radiusMiles = 50) => {
    setLoading(true);
    try {
      let url = `Locations/nearby-locations?lat=${lat}&lng=${lng}&radius=${radiusMiles}&limit=${maxResults}`;
      if (excludeId) {
        url += `&excludeLocationId=${excludeId}`;
      }
      const response = await axiosInstance.get(url);
      setNearbyLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching nearby locations:', error);
      setNearbyLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchAddress.trim()) return;
    
    setLoading(true);
    try {
      // Use Mapbox Geocoding API to convert address to coordinates
      const encodedAddress = encodeURIComponent(searchAddress);
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${mapboxToken}&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        setMapCenter({ lat, lng });
        setCenterLocation(null); // Clear any previous center location
        
        // Move the map to new location
        if (mapRef.current) {
          mapRef.current.flyTo({
            center: [lng, lat],
            zoom: 12,
            duration: 1000,
          });
        }
        
        fetchNearbyLocations(lat, lng, limit);
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleMyLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter({ lat: latitude, lng: longitude });
          setCenterLocation(null);
          setSearchAddress('');
          
          if (mapRef.current) {
            mapRef.current.flyTo({
              center: [longitude, latitude],
              zoom: 12,
              duration: 1000,
            });
          }
          
          fetchNearbyLocations(latitude, longitude, limit);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLoading(false);
        }
      );
    }
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    if (!isNaN(newLimit) && newLimit > 0 && newLimit <= 500) {
      setLimit(newLimit);
      if (mapCenter) {
        fetchNearbyLocations(mapCenter.lat, mapCenter.lng, newLimit, centerLocation?.id, radius);
      }
    }
  };

  const handleMapMoveEnd = useCallback(() => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const bounds = mapRef.current.getBounds();
      
      const ne = bounds.getNorthEast();
      const centerLat = center.lat;
      const centerLng = center.lng;
      
      // Calculate radius from center to edge of visible map (in miles)
      const latDiff = Math.abs(ne.lat - centerLat);
      const lngDiff = Math.abs(ne.lng - centerLng);
      
      // Convert to miles (1 degree latitude ≈ 69 miles)
      const latMiles = latDiff * 69;
      const lngMiles = lngDiff * 69 * Math.cos(centerLat * Math.PI / 180);
      
      // Use the smaller to fit within visible area
      const radiusMiles = Math.max(1, Math.round(Math.min(latMiles, lngMiles) * 0.9));
      
      setRadius(radiusMiles);
      setMapCenter({ lat: centerLat, lng: centerLng });
      fetchNearbyLocations(centerLat, centerLng, limit, centerLocation?.id, radiusMiles);
    }
  }, [limit, centerLocation]);

  const handleMapClick = useCallback((event) => {
    if (!mapRef.current) return;
    
    const features = mapRef.current.queryRenderedFeatures(event.point, {
      layers: ['clusters', 'unclustered-point']
    });
    
    if (!features.length) return;
    
    const feature = features[0];
    
    if (feature.layer.id === 'clusters') {
      // Zoom into cluster
      const clusterId = feature.properties.cluster_id;
      const source = mapRef.current.getSource('locations');
      
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        
        mapRef.current.easeTo({
          center: feature.geometry.coordinates,
          zoom: zoom
        });
      });
    } else if (feature.layer.id === 'unclustered-point') {
      // Navigate to location
      const locationId = feature.properties.id;
      navigate(`/locations/${locationId}`);
    }
  }, [navigate]);

  const handleMouseMove = useCallback((event) => {
    if (!mapRef.current) return;
    
    // Clear any pending close timeout when moving over the map
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    const features = mapRef.current.queryRenderedFeatures(event.point, {
      layers: ['clusters', 'unclustered-point']
    });
    
    if (features.length > 0) {
      const feature = features[0];
      
      if (feature.layer.id === 'clusters') {
        // Get cluster leaves (locations in this cluster)
        const clusterId = feature.properties.cluster_id;
        const source = mapRef.current.getSource('locations');
        const pointCount = feature.properties.point_count;
        
        source.getClusterLeaves(clusterId, Math.min(pointCount, 5), 0, (err, leaves) => {
          if (err) return;
          
          setHoverInfo({
            longitude: feature.geometry.coordinates[0],
            latitude: feature.geometry.coordinates[1],
            isCluster: true,
            pointCount: pointCount,
            locations: leaves.map(leaf => leaf.properties)
          });
        });
      } else {
        // Single point
        setHoverInfo({
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          isCluster: false,
          properties: feature.properties
        });
      }
    } else {
      // Delay closing popup when mouse leaves markers
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setHoverInfo(null);
      }, 300);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Delay closing popup when mouse leaves the map
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverInfo(null);
    }, 300);
  }, []);

  const handlePopupMouseEnter = useCallback(() => {
    // Cancel any pending close when entering popup
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handlePopupMouseLeave = useCallback(() => {
    // Close popup when leaving it
    setHoverInfo(null);
  }, []);

  const handleLocationClick = (location) => {
    navigate(`/locations/${location.id}`);
  };

  const formatAddress = (location) => {
    const parts = [
      location.address1,
      location.city,
      location.state,
      location.postal_code,
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <>
      <LocationsTabs tab="locations_map" />
      <div className={classes.container}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <Box className={classes.controls}>
              <div className={classes.searchContainer}>
                <TextField
                  className={classes.searchField}
                  placeholder="Search address..."
                  variant="outlined"
                  size="small"
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleSearch} edge="end" size="small">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Tooltip title="Use my location">
                  <IconButton onClick={handleMyLocation} color="primary">
                    <MyLocationIcon />
                  </IconButton>
                </Tooltip>
              </div>
              
              <div className={classes.rightControls}>
                {loading && (
                  <div className={classes.loading}>
                    <CircularProgress size={16} />
                    <span>Loading...</span>
                  </div>
                )}
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div className={classes.legendDot} style={{ backgroundColor: '#1976d2' }} />
                    <span>Locations ({nearbyLocations.length})</span>
                  </div>
                  <span className={classes.panHint}>Radius: {radius} mi • Pan/zoom to adjust</span>
                </div>
                <TextField
                  label="Max Locations"
                  type="number"
                  size="small"
                  value={limit}
                  onChange={handleLimitChange}
                  className={classes.limitInput}
                  inputProps={{ min: 1, max: 500 }}
                />
              </div>
            </Box>
            
            <div className={classes.mapContainer}>
              {mapCenter && (
                <Map
                  mapLib={import('mapbox-gl')}
                  ref={mapRef}
                  initialViewState={{
                    longitude: mapCenter.lng,
                    latitude: mapCenter.lat,
                    zoom: 12,
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  mapStyle="mapbox://styles/mapbox/light-v10"
                  mapboxAccessToken={mapboxToken}
                  attributionControl={false}
                  onLoad={handleMapMoveEnd}
                  onMoveEnd={handleMapMoveEnd}
                  onClick={handleMapClick}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  interactiveLayerIds={['clusters', 'unclustered-point']}
                >
                  {/* Search radius circle */}
                  {mapCenter && radius > 0 && (
                    <Source id="radius-circle" type="geojson" data={createCircle(mapCenter, radius)}>
                      <Layer
                        id="radius-circle-fill"
                        type="fill"
                        paint={{
                          'fill-color': '#1976d2',
                          'fill-opacity': 0.08
                        }}
                      />
                      <Layer
                        id="radius-circle-line"
                        type="line"
                        paint={{
                          'line-color': '#1976d2',
                          'line-width': 2,
                          'line-dasharray': [2, 2]
                        }}
                      />
                    </Source>
                  )}
                  
                  {/* Clustered location markers */}
                  {nearbyLocations.length > 0 && (
                    <Source
                      id="locations"
                      type="geojson"
                      data={{
                        type: 'FeatureCollection',
                        features: nearbyLocations
                          .filter(loc => !isNaN(parseFloat(loc.latitude)) && !isNaN(parseFloat(loc.longitude)))
                          .map(location => ({
                            type: 'Feature',
                            properties: {
                              id: location.id,
                              name: location.name || location.organization_name || 'Location',
                              address: formatAddress(location),
                              distance_miles: location.distance_miles,
                              provider_count: location.provider_count || 0,
                              contact_count: location.contact_count || 0,
                              organization_id: location.organization_id,
                            },
                            geometry: {
                              type: 'Point',
                              coordinates: [parseFloat(location.longitude), parseFloat(location.latitude)]
                            }
                          }))
                      }}
                      cluster={true}
                      clusterMaxZoom={14}
                      clusterRadius={50}
                    >
                      {/* Cluster circles */}
                      <Layer
                        id="clusters"
                        type="circle"
                        filter={['has', 'point_count']}
                        paint={{
                          'circle-color': '#1976d2',
                          'circle-radius': [
                            'step',
                            ['get', 'point_count'],
                            20,   // radius for count < 10
                            10, 25,  // radius for count >= 10
                            25, 30   // radius for count >= 25
                          ],
                          'circle-stroke-width': 2,
                          'circle-stroke-color': '#fff'
                        }}
                      />
                      
                      {/* Cluster count labels */}
                      <Layer
                        id="cluster-count"
                        type="symbol"
                        filter={['has', 'point_count']}
                        layout={{
                          'text-field': '{point_count_abbreviated}',
                          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                          'text-size': 12
                        }}
                        paint={{
                          'text-color': '#ffffff'
                        }}
                      />
                      
                      {/* Individual unclustered points */}
                      <Layer
                        id="unclustered-point"
                        type="circle"
                        filter={['!', ['has', 'point_count']]}
                        paint={{
                          'circle-color': '#1976d2',
                          'circle-radius': 8,
                          'circle-stroke-width': 2,
                          'circle-stroke-color': '#fff'
                        }}
                      />
                    </Source>
                  )}
                  
                  {/* Hover popup */}
                  {hoverInfo && (
                    <Popup
                      longitude={hoverInfo.longitude}
                      latitude={hoverInfo.latitude}
                      anchor="bottom"
                      closeButton={false}
                      closeOnClick={false}
                      offset={15}
                      style={{ zIndex: 1000 }}
                      maxWidth="320px"
                    >
                      <div 
                        style={{ maxHeight: 300, overflowY: 'auto' }}
                        onMouseEnter={handlePopupMouseEnter}
                        onMouseLeave={handlePopupMouseLeave}
                      >
                        {hoverInfo.isCluster ? (
                          <>
                            <div className={classes.tooltipName}>
                              {hoverInfo.pointCount} Locations
                            </div>
                            {hoverInfo.locations && hoverInfo.locations.map((loc, idx) => (
                              <div key={idx} className={classes.clusterItem}>
                                <div className={classes.clusterItemName}>{loc.name}</div>
                                <div className={classes.tooltipAddress}>{loc.address}</div>
                                <div className={classes.clusterItemCounts}>
                                  {loc.provider_count > 0 && <span className={classes.providerBadge}>{loc.provider_count} {loc.provider_count === 1 ? 'Provider' : 'Providers'}</span>}
                                  {loc.contact_count > 0 && <span className={classes.contactBadge}>{loc.contact_count} {loc.contact_count === 1 ? 'Contact' : 'Contacts'}</span>}
                                  {loc.organization_id && <span className={classes.orgBadge}>1 Organization</span>}
                                </div>
                              </div>
                            ))}
                            {hoverInfo.pointCount > 5 && (
                              <div className={classes.moreText}>
                                + {hoverInfo.pointCount - 5} more... (click to zoom)
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className={classes.tooltipName}>
                              {hoverInfo.properties.name}
                            </div>
                            <div className={classes.tooltipAddress}>
                              {hoverInfo.properties.address}
                            </div>
                            <div className={classes.tooltipStats}>
                              {hoverInfo.properties.distance_miles} mi
                              {hoverInfo.properties.provider_count > 0 && ` • ${hoverInfo.properties.provider_count} providers`}
                              {hoverInfo.properties.contact_count > 0 && ` • ${hoverInfo.properties.contact_count} contacts`}
                              {hoverInfo.properties.organization_id && ` • 1 organization`}
                            </div>
                          </>
                        )}
                      </div>
                    </Popup>
                  )}
                  
                  {/* Center marker - green */}
                  <Marker
                    longitude={mapCenter.lng}
                    latitude={mapCenter.lat}
                    anchor="bottom"
                  >
                    <LocationOnIcon
                      className={classes.centerMarker}
                      style={{ fontSize: 42 }}
                    />
                  </Marker>
                </Map>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

LocationsMapPage.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styled = withStyles(styles)(LocationsMapPage);
export { styled as LocationsMapPage };